import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core'
import { Subject, fromEvent } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

/**
 * Directive to make an element sticky below a reference element.
 * Automatically calculates and updates the `top` CSS property based on the
 * bottom edge of the reference element.
 *
 * Usage:
 * <div class="sticky-header" stickyBelow="reference-element-id">...</div>
 */
@Directive({
  selector: '[stickyBelow]',
  standalone: false,
})
export class StickyBelowDirective implements OnInit, OnDestroy {
  @Input('stickyBelow') referenceElementId: string

  private destroy$ = new Subject<void>()

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    // Initial calculation after a short delay to ensure DOM is ready
    setTimeout(() => this.updateStickyTop(), 0)

    // Recalculate on window resize
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.updateStickyTop())

    // Recalculate on scroll (in case reference element changes position)
    fromEvent(window, 'scroll')
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe(() => this.updateStickyTop())
  }

  private updateStickyTop(): void {
    if (!this.referenceElementId) {
      return
    }

    const referenceElement = document.getElementById(this.referenceElementId)
    if (!referenceElement) {
      console.warn(`StickyBelowDirective: Element with ID "${this.referenceElementId}" not found`)
      return
    }

    const rect = referenceElement.getBoundingClientRect()
    const computedStyle = window.getComputedStyle(referenceElement)
    const position = computedStyle.position

    // If the reference element is sticky/fixed, use its bottom position
    // Otherwise, calculate based on its static position
    let topValue: number

    if (position === 'sticky' || position === 'fixed') {
      topValue = rect.bottom
    } else {
      // For static elements, get the offset from the top of the document
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      topValue = rect.bottom + scrollTop
    }

    this.renderer.setStyle(this.el.nativeElement, 'top', `${topValue}px`)
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
